/**
 * MarketplaceDialogs - Dialogs para funcionalidades do Marketplace
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Componentes:
 * - FeatureConfirmDialog: Dialog para confirmar destaque de produto
 * - PixPaymentDialog: Dialog para exibir pagamento PIX
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - Dialog, Button, Input, Label, Badge (shadcn/ui)
 */

import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useTranslation } from "../translations";

/**
 * FeatureConfirmDialog - Dialog para destacar produto no marketplace
 */
export const FeatureConfirmDialog = ({ item, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    document: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(formData);
  };

  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>🔥 Destacar Produto</DialogTitle>
          <DialogDescription>
            Destaque seu produto por 15 dias por apenas R$ 39,90 via PIX
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">{t('mkdFullName')} *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Ex: João Silva Santos"
              required
            />
          </div>
          <div>
            <Label htmlFor="email">{t('mkdEmail')} *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="Ex: joao@fazenda.com.br"
              required
            />
          </div>
          <div>
            <Label htmlFor="document">CPF/CNPJ *</Label>
            <Input
              id="document"
              value={formData.document}
              onChange={(e) => setFormData({...formData, document: e.target.value.replace(/\D/g, '')})}
              placeholder="Apenas números"
              maxLength={14}
              required
            />
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-green-800">{t('mkdProduct')}: {item.title}</h3>
                <p className="text-sm text-green-600">15 dias em destaque</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-green-700">R$ 39,90</p>
                <p className="text-xs text-green-600">{t('mkdPaymentViaPix')}</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              {t('cancel')}
            </Button>
            <Button type="submit" className="bg-green-600 hover:bg-green-700">
              {t('generatePix')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * PixPaymentDialog - Dialog para exibir QR Code e informações de pagamento PIX
 */
export const PixPaymentDialog = ({ payment, status, onClose }) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (payment?.expires_at) {
      const updateTimer = () => {
        const now = new Date().getTime();
        const expiry = new Date(payment.expires_at).getTime();
        const difference = expiry - now;
        
        if (difference > 0) {
          const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((difference % (1000 * 60)) / 1000);
          setTimeLeft(`${minutes}m ${seconds}s`);
        } else {
          setTimeLeft('Expirado');
        }
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
  }, [payment?.expires_at]);

  const copyPixCode = () => {
    navigator.clipboard.writeText(payment.pix_code);
    alert('Código PIX copiado!');
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-2">
              💳
            </span>
            Pagamento PIX
          </DialogTitle>
          <DialogDescription>
            {status?.status === 'paid' ? 
              'Pagamento confirmado! Produto destacado com sucesso.' :
              'Escaneie o QR Code ou copie o código PIX para pagar'
            }
          </DialogDescription>
        </DialogHeader>
        
        {status?.status === 'paid' ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">✅</span>
            </div>
            <h3 className="text-lg font-semibold text-green-800 mb-2">
              Pagamento Confirmado!
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Seu produto agora está em destaque por 15 dias
            </p>
            <Button onClick={onClose} className="bg-green-600 hover:bg-green-700">
              {t('close')}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-center">
              <div className="bg-white p-4 rounded-lg border mb-4">
                {payment.qr_code_base64 ? (
                  <img 
                    src={`data:image/png;base64,${payment.qr_code_base64}`}
                    alt="QR Code PIX"
                    className="mx-auto w-48 h-48"
                  />
                ) : (
                  <div className="w-48 h-48 bg-gray-100 rounded flex items-center justify-center mx-auto">
                    <span className="text-4xl">📱</span>
                  </div>
                )}
              </div>
              
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{t('mkdValue')}:</span>
                  <span className="text-lg font-bold text-green-600">R$ {payment.amount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{t('mkdStatusLabel')}:</span>
                  <Badge variant={status?.status === 'pending' ? 'secondary' : 'success'}>
                    {status?.status === 'pending' ? '⏳ Aguardando' : status?.status}
                  </Badge>
                </div>
                {timeLeft && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{t('mkdExpiresIn')}:</span>
                    <span className="text-sm text-orange-600 font-medium">{timeLeft}</span>
                  </div>
                )}
              </div>

              <Button 
                onClick={copyPixCode}
                variant="outline" 
                className="w-full mb-2"
              >
                📋 Copiar Código PIX
              </Button>
              
              <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
                <p className="font-medium mb-1">📱 Como pagar:</p>
                <p>1. Abra o app do seu banco</p>
                <p>2. Escolha PIX → Pagar com QR Code</p>
                <p>3. Escaneie o código ou cole o código PIX</p>
                <p>4. Confirme o pagamento</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="flex-1">
                {t('close')}
              </Button>
              <Button 
                onClick={() => window.location.reload()} 
                className="flex-1"
              >
                ✅ Paguei
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
